import { Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AnnotationDefinition,
  AnnotationDefinitionCriterion,
  AnnotationOnImage,
  AnnotationSequence,
} from '../../models/annotation.types';
import { ExamScoringSchema } from '../../models/assessment.types';
import { useStyles } from './AnnotationTool.styles';
import { AnnotationToolList } from './AnnotationToolList.component';
import { CloseIcon } from './Menu/CloseIcon.component';
import Draggable, { DraggableEvent } from 'react-draggable';
import AnnotationIcon from '../../assets/icons/annotationIcon.svg';
import { IComment } from '../Comments/Comments.types';

interface AnnotationToolProps {
  isOpen: boolean;
  handleAnnotationsOpen: () => void;
  activeCriterion: ExamScoringSchema | null;
  activeAnnotationId: string;
  onActivateAnnotation: (id: string) => void;
  isAnnotationToolDocked: (value: boolean) => void;
  noAnswer: boolean | null;
  annotationsDefinition: AnnotationDefinition[];
  annotationsCriterion: AnnotationSequence[] | null;
  annotationOnPage: AnnotationOnImage[] | null;
  isSorted: boolean;
  canSeeAnnotationTool: boolean;
  isFullscreen: boolean;
  isCommentOpen: boolean;
  comments?: IComment[];
}

interface IPosition {
  deltaX?: number;
  deltaY?: number;
  lastX?: number;
  lastY?: number;
  x: number;
  y: number;
}

export const AnnotationTool: React.FC<AnnotationToolProps> = (props) => {
  const classes = useStyles();
  const [hiddenContainerHeight, setHiddenContainerHeight] = useState<string>('calc(100vh - 300px)');

  const annotationDefinitionForCriterion = useMemo(() => {
    return props.annotationsDefinition.filter((o2) =>
      (props.annotationsCriterion !== null ? props.annotationsCriterion : []).some(
        (o1) => o2.id === o1.annotationDefinitionId,
      ),
    );
  }, [props.annotationsCriterion]);

  const connectedList: AnnotationDefinitionCriterion[] = useMemo(() => {
    const annotationsOnImageAssignedToCriterion: AnnotationSequence[] = (
      props.annotationsCriterion !== null ? props.annotationsCriterion : []
    ).filter((o2) => annotationDefinitionForCriterion.some((o1) => o2.annotationDefinitionId === o1.id));

    return annotationDefinitionForCriterion.map((t1) => ({
      ...t1,
      ...annotationsOnImageAssignedToCriterion.find((t2) => t2.annotationDefinitionId === t1.id),
    }));
  }, [props.annotationsCriterion, annotationDefinitionForCriterion]);

  const sortedList = useMemo(() => {
    return connectedList.sort(
      (a: { sequence?: string }, b: { sequence?: string }) => Number(a.sequence) - Number(b.sequence),
    );
  }, [connectedList]);

  const positionX = -27;
  const defaultPosition: IPosition = { x: positionX, y: 0, deltaX: positionX, deltaY: 0 };

  const [positions, setPositions] = useState<IPosition>(defaultPosition);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isDocked, setIsDocked] = useState<boolean>(true);
  const [showBorder, setShowBorder] = useState<boolean>(false);

  if (localStorage.getItem('positions_annotation_tool') === null) {
    localStorage.setItem(`positions_annotation_tool`, JSON.stringify(positions));
  }

  useEffect(() => {
    const existingDivPositions = JSON.parse(localStorage.getItem('positions_annotation_tool') || '{}');
    if (JSON.stringify(existingDivPositions) === '{}') {
      setPositions(defaultPosition);
      setIsDocked(true);
    }
    if (existingDivPositions.x !== defaultPosition.x) {
      setPositions(existingDivPositions);
      setIsDocked(false);
    } else {
      setIsDocked(true);
    }
    setHasLoaded(true);
  }, []);

  const eventHandler = (e: DraggableEvent | MouseEvent, data: IPosition) => {
    setPositions({ x: data.x, y: data.y });
    if (data.x && data.x <= positionX) {
      setPositions(defaultPosition);
      setIsDocked(true);
      setShowBorder(false);
    } else {
      setIsDocked(false);
    }
  };

  const eventHandlerDrag = (e: DraggableEvent | MouseEvent, data: IPosition) => {
    if (data.x && data.x < 20 && data.x < -27) {
      setShowBorder(true);
    } else {
      setShowBorder(false);
    }
  };

  useEffect(() => {
    localStorage.setItem(`positions_annotation_tool`, JSON.stringify(positions));
  }, [positions]);

  useEffect(() => {
    props.isAnnotationToolDocked(isDocked);
  }, [isDocked]);

  useEffect(() => {
    if (!props.isFullscreen && positions.x > window.innerWidth - 500) {
      setPositions({ x: positions.x - 400, y: positions.y });
    } else if (props.isFullscreen && positions.x > window.innerWidth / 2 && positions.x < window.innerHeight) {
      setPositions({ x: positions.x + 400, y: positions.y });
    }
  }, [props.isFullscreen]);

  const calculateToolbarHeight = useCallback(() => {
    const el = document.getElementById('comments-list');
    const commentsHeight = el?.parentElement?.clientHeight ?? 0;

    let newHeight: string;

    if (commentsHeight > 0) {
      const viewportHeight = window.innerHeight;
      const baseOffset = 250;
      const additionalHeightOffset = 50;
      const calculatedHeight = viewportHeight - baseOffset - commentsHeight - additionalHeightOffset;
      const minHeight = 200;
      const finalHeight = Math.max(calculatedHeight, minHeight);
      newHeight = `${finalHeight}px`;
    } else {
      newHeight = 'calc(100vh - 300px)';
    }

    setHiddenContainerHeight((prev) => (prev !== newHeight ? newHeight : prev));
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculateToolbarHeight();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [props.isCommentOpen, props.comments, props.activeCriterion, calculateToolbarHeight]);

  useEffect(() => {
    calculateToolbarHeight();

    window.addEventListener('resize', calculateToolbarHeight);

    const el = document.getElementById('comments-list');
    const parentEl = el?.parentElement;

    let observer: ResizeObserver | null = null;
    if (parentEl) {
      observer = new ResizeObserver(() => calculateToolbarHeight());
      observer.observe(parentEl);
    }

    return () => {
      window.removeEventListener('resize', calculateToolbarHeight);
      observer?.disconnect();
    };
  }, [calculateToolbarHeight]);

  return (
    <>
      {props.isOpen && hasLoaded && (
        <Draggable
          handle="#header"
          defaultPosition={defaultPosition}
          position={positions}
          onStop={eventHandler}
          onDrag={eventHandlerDrag}
          bounds="parent"
        >
          <div
            className={isDocked ? classes.container : classes.containerDocked}
            style={{
              borderLeft: showBorder ? '3px solid #3560FF' : '',
              maxHeight: !isDocked ? `calc(${hiddenContainerHeight} + 50px)` : 'none',
            }}
          >
            <div className={isDocked ? classes.headerContainer : classes.headerContainerBlue}>
              <div className={classes.cursor} id="header">
                <div>
                  <CloseIcon onClick={props.handleAnnotationsOpen} className={!isDocked ? classes.closeIcon : ''} />
                </div>
              </div>
            </div>
            {!isDocked && (
              <div className={classes.headerAnnotation}>
                <img src={AnnotationIcon} alt="Adnotacje" />
                <Typography style={{ fontWeight: 600, marginLeft: '10px' }} variant="subtitle2">
                  Adnotacje
                </Typography>
              </div>
            )}
            <div
              style={{
                padding: '5px',
                overflowY: 'auto',
                height: hiddenContainerHeight,
                marginRight: '5px',
              }}
            >
              <AnnotationToolList
                list={!props.isSorted ? connectedList : sortedList}
                activeAnnotationId={props.activeAnnotationId}
                onActivateAnnotation={props.onActivateAnnotation}
                annotationOnPage={props.annotationOnPage}
                activeCriterion={props.activeCriterion}
                noAnswer={props.noAnswer}
                canSeeAnnotationTool={props.canSeeAnnotationTool}
              />
            </div>
          </div>
        </Draggable>
      )}
    </>
  );
};
